/* cc-analytics.js: storefront event bridge (WP-11)
   ------------------------------------------------------------------
   Listens to what the theme already does and republishes a small, fixed
   set of funnel events to Shopify Customer Events with
   Shopify.analytics.publish('cc_<name>', props). The custom pixel
   (docs/analytics/custom-pixel.js, pasted in Admin > Customer events)
   subscribes to them and forwards to GA4. Event list and payloads:
   docs/analytics/EVENTS.md.

   Rules this file keeps:
   - Passive only. It never calls preventDefault, never stops propagation,
     never changes the DOM and never throws. Block this file and the site
     behaves exactly the same.
   - No personal data. It never reads a text, select or contact input the
     shopper typed into. Every prop is checked against a per-key pattern
     (2-letter state, enum words, handles, numeric ids); anything else is
     dropped before publish.
   - Consent-aware. Uses the Shopify Customer Privacy API: events are
     dropped when analytics processing is not allowed, queued while the
     answer is unknown, and flushed once it is known. If the API never
     loads, the queue flushes after a short wait; Shopify's pixel manager
     still applies the custom pixel's own consent permission downstream.
   - Loaded by layout/theme.liquid behind settings.analytics_bridge.

   Sources (all already in the theme, untouched):
     cc:analytics  (theme.js, ki-pdp.js, ki-cart-drawer.js, recipe board)
                   quick_add {outcome:'added'} -> add_to_cart (+ apparel_preorder)
                   checkout_click              -> begin_checkout
     cc:gate:closed (snippets/age-gate.liquid) -> age_gate_pass
     click .gate__alt                          -> age_gate_exit
     cc:state                                  -> state_step_result
     submit of a customer form tagged *notify* -> notify_signup
     click a COA page or PDF link              -> view_coa
     click the recipe dialog Shop link (recipe_shop) or a product
       link inside a recipe area                -> recipe_to_pdp
     click [data-checkout] outside the drawer  -> begin_checkout
     submit of the trade form (.tf__form)      -> wholesale_enquiry
   purchase is not a theme event: the pixel takes it from Shopify's
   standard checkout_completed (see EVENTS.md). */
(function () {
  'use strict';
  if (window.__ccAnalytics) return; /* idempotent: one bridge per page */
  window.__ccAnalytics = true;

  var doc = document;
  var DEBUG = /[?&]cc_debug=1(?:&|$)/.test(location.search);
  var PREFIX = 'cc_';

  /* ── payload whitelist ─────────────────────────────────────────── */
  var PAT = {
    state: /^[A-Z]{2}$/,
    result: /^(ok|cbd-only|blocked|unconfigured)$/,
    source: /^[a-z0-9_-]{1,40}$/,
    placement: /^[a-z0-9_-]{1,40}$/,
    step: /^(answered|skipped|none)$/,
    handle: /^[a-z0-9][a-z0-9-]{0,99}$/,
    product_handle: /^[a-z0-9][a-z0-9-]{0,99}$/,
    variant_id: /^\d{1,20}$/,
    quantity: /^\d{1,3}$/,
    base: /^(joe|tini)$/,
    dest: /^(lineup|pdp)$/,
    kind: /^(page|pdf)$/,
    doc: /^[a-z0-9._-]{1,80}$/,
    request: /^(tasting|terms|other)$/,
    preorder: /^(on)$/
  };
  var KEYS = {
    age_gate_pass: ['step', 'state', 'result'],
    age_gate_exit: [],
    state_step_result: ['result', 'state', 'source'],
    notify_signup: ['source', 'state', 'product_handle'],
    view_coa: ['kind', 'doc', 'source'],
    recipe_to_pdp: ['base', 'dest', 'product_handle', 'source'],
    add_to_cart: ['handle', 'variant_id', 'quantity', 'preorder'],
    begin_checkout: ['placement'],
    wholesale_enquiry: ['request'],
    apparel_preorder: ['handle', 'variant_id', 'quantity']
  };

  function clean(name, props) {
    var out = {};
    var keys = KEYS[name] || [];
    props = props || {};
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var v = props[k];
      if (v === undefined || v === null || v === '') continue;
      v = String(v);
      if (v.indexOf('@') > -1) continue; /* belt and braces: never an address */
      if (PAT[k] && PAT[k].test(v)) out[k] = v;
    }
    return out;
  }

  /* ── consent (Shopify Customer Privacy API) ───────────────────── */
  var queue = [];
  var QUEUE_MAX = 40;
  var waitedOut = false;

  function privacyApi() {
    var s = window.Shopify;
    return s && s.customerPrivacy && typeof s.customerPrivacy.analyticsProcessingAllowed === 'function'
      ? s.customerPrivacy : null;
  }
  /* 'yes' | 'no' | 'unknown' */
  function consent() {
    var cp = privacyApi();
    if (!cp) return waitedOut ? 'yes' : 'unknown';
    try { return cp.analyticsProcessingAllowed() ? 'yes' : 'no'; } catch (e) { return 'unknown'; }
  }
  function publisher() {
    var s = window.Shopify;
    return s && s.analytics && typeof s.analytics.publish === 'function' ? s.analytics : null;
  }

  function send(item) {
    var a = publisher();
    if (!a) return false;
    try { a.publish(PREFIX + item.name, item.props); } catch (e) {}
    if (DEBUG) { try { console.info('[cc-analytics]', PREFIX + item.name, item.props); } catch (e) {} }
    return true;
  }

  function flush() {
    var c = consent();
    if (c === 'no') { queue.length = 0; return; }
    if (c !== 'yes' || !publisher()) return;
    while (queue.length) send(queue.shift());
  }

  function emit(name, props) {
    try {
      if (!KEYS[name]) return;
      var item = { name: name, props: clean(name, props) };
      var c = consent();
      if (c === 'no') return;
      if (c === 'yes' && publisher() && !queue.length) { send(item); return; }
      if (queue.length < QUEUE_MAX) queue.push(item);
      flush();
    } catch (e) { /* telemetry never breaks the page */ }
  }

  /* Load the consent API if the banner has not already. */
  try {
    if (!privacyApi() && window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { flush(); });
    }
  } catch (e) {}
  doc.addEventListener('visitorConsentCollected', flush);
  /* The publisher and the API load async; retry briefly, then stop. */
  var tries = 0;
  var timer = setInterval(function () {
    tries++;
    if (tries >= 10) waitedOut = true; /* ~5s: API absent, Shopify still gates the pixel */
    flush();
    if (tries >= 40 || (!queue.length && waitedOut)) clearInterval(timer);
  }, 500);
  window.addEventListener('load', flush);

  /* ── helpers ──────────────────────────────────────────────────── */
  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }
  function pathOf(a) {
    try { return new URL(a.getAttribute('href') || '', location.href).pathname.toLowerCase(); } catch (e) { return ''; }
  }
  function productHandleFromPath(p) {
    var m = /\/products\/([a-z0-9-]+)/.exec(p);
    return m ? m[1] : '';
  }
  function gateConfigured() {
    try {
      var el = doc.querySelector('[data-gate-config]');
      if (!el) return true;
      var cfg = JSON.parse(el.textContent) || {};
      var has = function (v) { return Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim()); };
      return has(cfg.banned) || has(cfg.cbdOnly);
    } catch (e) { return true; }
  }

  /* ── age gate ─────────────────────────────────────────────────── */
  var gatePassSent = false;
  var stateAnswered = false;
  var stateSkipped = false;

  function sendGatePass(detail) {
    if (gatePassSent) return;
    gatePassSent = true;
    var d = detail || {};
    emit('age_gate_pass', {
      step: stateAnswered ? 'answered' : stateSkipped ? 'skipped' : 'none',
      state: stateAnswered ? d.state : '',
      result: stateAnswered ? (gateConfigured() ? d.ship : 'unconfigured') : ''
    });
  }
  doc.addEventListener('cc:gate:closed', function (e) { sendGatePass(e && e.detail); });
  /* The gate can close before this deferred file runs. Not on the
     post-notify landing (?cc-notify=1): age-gate.liquid sets
     ccGateClosedAt there to release popups, but no gate was passed. */
  function notifyLanding() {
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      return /[?&]cc-notify=1(?:&|$)/.test((nav && nav.name) || location.search);
    } catch (e) { return false; }
  }
  if (window.ccGateClosedAt && !notifyLanding()) sendGatePass({ state: window.ccState, ship: window.ccShip });

  /* ── state step ───────────────────────────────────────────────── */
  var lastState = '';
  doc.addEventListener('cc:state', function (e) {
    var d = (e && e.detail) || {};
    var source = d.source || 'age_gate';
    var key = source + ':' + d.state + ':' + d.ship;
    if (key === lastState) return;
    lastState = key;
    if (source === 'age_gate') stateAnswered = !!d.state;
    emit('state_step_result', {
      result: gateConfigured() ? d.ship : 'unconfigured',
      state: d.state,
      source: source
    });
  });

  /* ── forwarded theme events ───────────────────────────────────── */
  var pendingPreorder = null; /* { handle, at } set on submit of a pre-order form */
  doc.addEventListener('cc:analytics', function (e) {
    var d = (e && e.detail) || {};
    var p = d.props || {};
    if (d.name === 'quick_add' && p.outcome === 'added') {
      var pre = pendingPreorder && (Date.now() - pendingPreorder.at < 15000) &&
        (!p.handle || p.handle === pendingPreorder.handle);
      var qty = pendingPreorder && pre ? pendingPreorder.qty : '';
      emit('add_to_cart', { handle: p.handle, variant_id: p.variant_id, quantity: qty, preorder: pre ? 'on' : '' });
      if (pre) emit('apparel_preorder', { handle: p.handle || pendingPreorder.handle, variant_id: p.variant_id, quantity: qty });
      pendingPreorder = null;
    } else if (d.name === 'checkout_click') {
      emit('begin_checkout', { placement: p.placement || 'drawer' });
    }
    /* recipe_shop is taken from the click below: theme.js sends it with an
       empty handle, and the base (joe|tini) is only in the link's href. */
  });

  /* ── clicks (capture: runs even when a handler stops propagation) ── */
  doc.addEventListener('click', function (e) {
    try {
      var t = e.target;
      if (closest(t, '.gate__alt')) { emit('age_gate_exit', {}); return; }
      if (closest(t, '[data-gate-skip]')) stateSkipped = true;

      var a = closest(t, 'a[href]');
      if (!a) {
        return;
      }
      var p = pathOf(a);

      /* Recipe dialog Shop link: recipe-dialog.js points it at
         #lnx-panel-joe or #lnx-panel-tini for the open recipe. */
      if (a.getAttribute('data-cc-event') === 'recipe_shop') {
        var hb = /lnx-panel-(joe|tini)/.exec(a.getAttribute('href') || '');
        emit('recipe_to_pdp', { base: hb ? hb[1] : '', dest: 'lineup', source: 'recipe_dialog' });
        return;
      }

      /* COA: a coa-* page or any PDF link (metafield coa_url files) */
      var coaPage = /\/pages\/(coa-[a-z0-9-]+)/.exec(p);
      if (coaPage || /\.pdf$/.test(p)) {
        var last = p.split('/').pop() || '';
        try { last = decodeURIComponent(last); } catch (x) {}
        var file = coaPage ? coaPage[1] : last.replace(/\s+/g, '-').replace(/[^a-z0-9._-]/g, '');
        emit('view_coa', {
          kind: coaPage ? 'page' : 'pdf',
          doc: file,
          source: (location.pathname.split('/')[1] || 'home').toLowerCase().replace(/[^a-z0-9_-]/g, '')
        });
        return;
      }

      /* Recipe -> PDP: product links inside a recipe area. The dialog's
         own Shop link already arrives as recipe_shop via cc:analytics. */
      if (a.getAttribute('data-cc-event') !== 'recipe_shop' &&
          closest(a, '.rc, [data-rcd], [data-rc-lib], .recipe-article, [data-recipe]')) {
        var h = productHandleFromPath(p);
        if (h) emit('recipe_to_pdp', { dest: 'pdp', product_handle: h, base: /tini/.test(h) ? 'tini' : /joe/.test(h) ? 'joe' : '', source: 'recipe' });
        return;
      }

      /* Checkout links outside the drawer (theme.js covers the drawer). */
      if (a.hasAttribute('data-checkout') && !closest(a, '[data-cart-scrim]')) {
        emit('begin_checkout', { placement: 'cart_page' });
      }
    } catch (err) {}
  }, true);

  /* ── form submits (capture; values read only from hidden fields) ── */
  doc.addEventListener('submit', function (e) {
    try {
      var f = e.target;
      if (!f || f.tagName !== 'FORM') return;

      /* Pre-order marker: the apparel buy box (WP-12) adds the hidden
         properties[Ships] line-item input, or data-cc-preorder. */
      if (f.hasAttribute('data-product-form') &&
          (f.hasAttribute('data-cc-preorder') || f.querySelector('input[type="hidden"][name="properties[Ships]"]'))) {
        var btn = f.querySelector('[name="add"]');
        var q = f.querySelector('input[name="quantity"]');
        pendingPreorder = {
          handle: (btn && btn.getAttribute('data-cc-handle')) || '',
          qty: q ? String(parseInt(q.value, 10) || 1) : '1',
          at: Date.now()
        };
        return;
      }

      /* Wholesale / trade form: the request type, nothing typed. */
      if (f.classList.contains('tf__form')) {
        var pick = f.querySelector('input[data-trade-choice]:checked');
        var r = pick ? pick.getAttribute('data-trade-choice') : 'other';
        emit('wholesale_enquiry', { request: r === 'tasting' || r === 'terms' ? r : 'other' });
        return;
      }

      /* Notify sign-ups: customer forms whose hidden tags mention notify. */
      var tagsEl = f.querySelector('input[type="hidden"][name="contact[tags]"]');
      var tags = tagsEl ? String(tagsEl.value || '') : '';
      if (!/notify/.test(tags)) return;
      var st = /(?:^|[,\s])state-([A-Z]{2})(?:$|[,\s])/.exec(tags);
      var prod = /(?:^|[,\s])product-([a-z0-9-]+)/.exec(tags);
      var source = f.id === 'gate-notify-form' || /age-gate-notify/.test(tags) ? 'age_gate'
        : f.hasAttribute('data-ccsr-notify') ? (f.id.indexOf('drawer') > -1 ? 'cart_drawer' : 'cart_page')
        : f.hasAttribute('data-kp-notify') ? 'pdp'
        : 'other';
      emit('notify_signup', {
        source: source,
        state: st ? st[1] : (source.indexOf('cart') === 0 ? window.ccState : ''),
        product_handle: prod ? prod[1] : ''
      });
    } catch (err) {}
  }, true);
})();
